import { RootState } from '@/store/store';
import { getComponentDiagnostics } from '@/components/pages/AssetsStudioPage/MainContent/Diagnostics/util';
import { useAppFilters } from '@/hooks/shared';
import { healthStatusToNumberMap } from '@/constants/shared';
import { ASSET_STUDIO } from '@/types/shared/navigation-tabs.enums';
import { Diagnostics } from '@/types/api/diagnostics.types';
import { isDateInRange } from '@/utils';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// Custom hook to handle diagnostic filtering and active chart point logic
export const useFilteredDiagnostics = (diagnostics: Diagnostics[]) => {
  const { selectedComponent } = useAppFilters();
  const [activeChartPoint, setActiveChartPoint] = useState<[string, number]>(['1999-01-21T00:00:00', 2]);

  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);

  const memoizedFrom = useMemo(() => new Date(from), [from]);
  const memoizedTo = useMemo(() => new Date(to), [to]);

  const filtered = useMemo(() => {
    const fromDateISO = format(new Date(memoizedFrom), "yyyy-MM-dd'T'HH:mm:ss");
    const toDateISO = format(new Date(memoizedTo), "yyyy-MM-dd'T'HH:mm:ss");
    return diagnostics?.filter((item: any) => isDateInRange(item?.[0], new Date(fromDateISO), new Date(toDateISO)));
  }, [memoizedFrom, memoizedTo, diagnostics, selectedComponent]);

  useEffect(() => {
    const componentDiagnostics = getComponentDiagnostics(filtered, selectedComponent);
    const newActiveEl = componentDiagnostics?.at(
      componentDiagnostics?.findIndex((el) => el[0] === activeChartPoint[0])
    );
    if (newActiveEl) {
      const { assets } = newActiveEl[1] || {};
      const healthStatus =
        selectedComponent === ASSET_STUDIO.ALL_COMPONENTS
          ? assets?.[0]?.health_status
          : assets?.[0]?.components?.find((el: any) => el.component_id === selectedComponent)?.health_status;

      setActiveChartPoint([newActiveEl[0], healthStatusToNumberMap[healthStatus]]);
    }
  }, [filtered, selectedComponent]);

  return { filteredDiagnostics: filtered, activeChartPoint, setActiveChartPoint };
};
